<template>
  <div class="card componentContainer position-relative" id="kt_chat_messenger">
    <div v-if="conversationId"
      :class="['card-header', 'd-inline-flex', 'align-items-center', { 'p-0': isSizePhoneMode, phoneSizeMargins: isSizePhoneMode }]"
      id="kt_chat_messenger_header">
      <div class="card-title">
        <div class="d-inline-flex justify-content-center me-3 flex-center gap-2">
          <!-- back button in mode phone size -->
          <button v-if="isSizePhoneMode" @click="back()" class="btn btn-icon btn-active-light-primary me-5 w-auto"
            type="button" :title="$t('back')">
            <img :src="`${base_url}/assets/image/arrow-${locale == 'ar' ? 'right' : 'left'}.svg`" width="15" alt="" />
          </button>
          <p class="user-name me-1 lh-1 m-0">{{ formattedMobileNumber }}</p>
          <ExpiryStatus :conversationExpirationTimestamp="conversationId?.conversation_expiration_timestamp" :preText="$t('expires-in')"/>
        </div>
      </div>
      <div class="card-toolbar m-0 d-sm-flex d-md-flex d-xl-none" @click="showProfile">
        <button type="button" class="btn btn-sm btn-light fs-7 m-0 h-auto">
          {{ $t("Customer data") }}
        </button>
      </div>
      <div class="card-toolbar m-0 d-sm-none d-md-none d-xl-flex" @click="toggleProfile">
        <button data-bs-toggle="tooltip" :title="$t(chatExpanded ? 'Show Profile' : 'Hide Profile')"
          class="btn p-0 w-auto ripple" type="button">
          <i :class="['bi', 'bi-code', 'fs-3', 'toggleProfile', { toggled: !chatExpanded }]"></i>
        </button>
      </div>
    </div>
    <div class="chat-container scroll-y position-relative" id="chatMessages" data-kt-element="messages"
      data-kt-scroll="true" data-kt-scroll-activate="{default: false, lg: true}" data-kt-scroll-max-height="auto"
      data-kt-scroll-dependencies="#kt_header, #kt_toolbar, #kt_footer, #kt_chat_messenger_header, #kt_chat_messenger_footer"
      data-kt-scroll-wrappers="#kt_content, #kt_chat_messenger_body" data-kt-scroll-offset="5px" @scroll="checkScroll">
      <div v-if="!conversationId" class="noConversation flex-grow-1 text-center d-flex">
        <div class="m-auto">
          <div class="noConversationContent">
            <img :src="`${base_url}/assets/image/gray-logo.svg`" width="145" class="d-block mb-10 mx-auto" />
            <h3>{{ $t("managingSalesOperationsOnWhatsApp") }}</h3>
            <p class="text-black-50">
              {{ $t("manageSalesOperationsOnWhatsAppViaLinker") }}
            </p>
          </div>
        </div>
      </div>
      <template v-else>
        <div class="conversationHeader d-flex align-items-center justify-content-center position-sticky z-index-3"
          v-if="currentLabel">
          <span class="bg-gray-300 pt-4 pb-4 pe-3 ps-3 rounded-3">
            {{ currentLabel }}
          </span>
        </div>
        <template v-for="(message, index) in firebaseMessages" :key="index">
          <template v-if="dateSeparationObject && dateSeparationObject[index]">
            <div class="d-flex align-items-center justify-content-center" :data-id-ref="`${index}`">
              <span class="bg-gray-300 pt-4 pb-4 pe-3 ps-3 rounded-3">
                {{ dateSeparationObject[index].date }}
              </span>
            </div>
          </template>
          <div :data-id-ref="`${index}`"
            :class="['d-flex', 'mb-10', 'w-100', { 'another-user-chat': !message.sent_by_merchant, 'your-chat': message.sent_by_merchant }]">
            <div
              :class="['d-flex', 'flex-column', 'w-100', { 'align-items-start': message.sent_by_merchant, 'align-items-end': !message.sent_by_merchant }]">
              <div class="d-flex align-items-center mb-2" v-if="message?.timestamp">
                <div class="d-flex align-items-center gap-1 mb-1">
                    <MessageStatus :data="message"/>
                    <span dir="auto" class="time text-muted fs-7">{{ formatTimestamp(message?.timestamp) }}
                  </span>
                </div>
              </div>
              <MessageWrapper :message="message">
                <div v-if="message?.reply_message?.id">
                  <ReceivedReply :message="message" :conversation="conversationId" />
                </div>
                <div class="w-100" v-else>
                  <div v-if="(message.type == MessageTypes.Text && !message.linker_payment_status) || (message.type == MessageTypes.Template && message?.extra_data?.template?.is_system && !message.linker_payment_status)"
                    class="message-text p-5 rounded text-dark fw-bold mw-lg-400px fs-14px"
                    data-kt-element="message-text" dir="auto" @contextmenu.prevent="showMessageMenuOptions(message)">
                      <span v-html="message.type == MessageTypes.Text ? convertToHyperlink(message.content) : message?.extra_data?.template?.components?.[1]?.text"></span>
                  </div>
                  <PaymentResponseWrapper v-else-if="(message.type == MessageTypes.Text && !!message.linker_payment_status) || (message.type == MessageTypes.Template && message?.extra_data?.template?.is_system)"
                     :message="message"/>
                  <OrderType v-else-if="message.type == MessageTypes.Order" @payment-link="sendPaymentLink"
                    :url="`${locale}/linked_marketplace/${message.linker_linked_marketplace_id}/orders/show/${message.linker_order_id}`"
                    :message="message" :data_id_ref="index" />
                  <AudioType class="audio-container"
                    :class="[{ 'border-top-left-0': !message.sent_by_merchant, 'border-top-right-0': message.sent_by_merchant }]"
                    v-else-if="message.type == MessageTypes.Audio" :message="message">
                  </AudioType>
                  <div class="image-container"
                    :class="[{ 'border-top-left-0': !message.sent_by_merchant, 'border-top-right-0': message.sent_by_merchant }]"
                    v-else-if="message.type == MessageTypes.Image">
                    <ImageType :message="message" />
                  </div>
                  <div v-else-if="message.type == MessageTypes.Interactive"
                    class="catalog-container border-top-right-0">
                    <Catalog v-if="message.content.interactive.type == InteractiveSubtypes.Catalog" :message="message"
                      :profile="profile" />
                    <ProductShare v-else-if="message.content.interactive.type == InteractiveSubtypes.Product"
                      :message="message" :profile="profile" />
                    <ProductsShare v-else-if="message.content.interactive.type == InteractiveSubtypes.Products"
                      :message="message" :profile="profile" />
                    <PaymentDetails v-else-if="message.content.interactive.type == InteractiveSubtypes.PaymentSummary"
                      :message="message" />
                    <Flow v-else-if="message.content.interactive.type == InteractiveSubtypes.Flow" :message="message" />
                    <InteractiveButtons
                      v-else-if="message.content.interactive.type == InteractiveSubtypes.InteractiveButtons"
                      :message="message" />
                    <div class="message-text p-5 rounded text-dark fw-bold mw-lg-400px fs-14px"
                      v-else-if="message.content.interactive.type == InteractiveSubtypes.ButtonReply">
                      {{ message.content?.button_reply?.title }}
                    </div>
                    <div class="message-text p-5 rounded text-dark fw-bold mw-lg-400px fs-14px"
                      v-else-if="message.content.interactive.type == InteractiveSubtypes.nfmReply">
                      {{ message.content?.nfm_reply?.body }}
                    </div>
                    <NotSupported v-else />
                  </div>
                  <div v-else-if="message.type == MessageTypes.Document">
                    <Document :message="message" />
                  </div>
                  <div v-else-if="message.type === MessageTypes.Template">
                    <Campaign :message="message" @showTemplate="$emit('showTemplate',$event)"/>
                  </div>
                  <div v-else-if="message.type == MessageTypes.Location">
                    <Location :message="message" />
                  </div>
                  <div class="image-container"
                     :class="[{ 'border-top-left-0': !message.sent_by_merchant, 'border-top-right-0': message.sent_by_merchant }]"
                     v-else-if="message.type == MessageTypes.Video">
                    <VideoType :message="message" />
                  </div>
                  <NotSupported v-else />
                </div>
                  <MessageOptionsMenu :message="message" ref="messageMenuOptionsRef"
                                      :activeMenu="activeMenu"
                                      @set-active-menu="activeMenu = $event"/>
                <p class="message-error mw-lg-400px" v-if="message?.message_errors?.length > 0">
                    {{message?.message_errors?.[0]?.error_data?.details}}</p>

              </MessageWrapper>
            </div>
          </div>
        </template>
      </template>
    </div>
    <button v-if="conversationId && hasNewMessageUnRead"
      class="btn btn-icon btn-active-light-primary me-1 position-absolute unreadMessages" type="button"
      data-bs-toggle="tooltip" :title="$t('unreadMessages')" @click="srollAndRead()">
      <i class="bi bi-arrow-down-circle fs-3x text-success"></i>
    </button>
    <div v-if="conversationId" class="card-footer p-6 position-relative" id="kt_chat_messenger_footer">
      <ReplyMessage v-if="replyToMessage != null" />
      <textarea v-if="!showRecordSection" dir="auto" class="form-control form-control-flush mb-3 resize-none" rows="2" data-kt-element="input"
        v-model="newMessage" :placeholder="$t('Type a message')" @keydown.enter.prevent="sendOnEnter" :disabled="waitingForFileMessage"></textarea>
      <div v-show="!showRecordSection" class="chatActions">
        <div class="d-flex align-items-center me-2" v-if="uploadedImagesLength == 0 && !waitingForFileMessage">
          <!-- <button
            class="btn btn-sm btn-icon btn-active-light-primary me-1"
            type="button"
            data-bs-toggle="tooltip"
            title="Coming soon"
          >
            <i class="bi bi-paperclip fs-3"></i>
          </button> -->
          <div class="position-relative" id="#kt_header_menu_chat">
            <div style="height: fit-content;">
                <button ref="menu_button" @click="toggleShowMenuUpload"
                  class="btn btn-sm btn-icon bg-gray-200 bg-hover-light-danger text-hover-danger me-1 menu-link w-35px h-35px"
                  type="button" data-bs-toggle="tooltip" :title="$t('upload')" data-kt-menu-trigger="click"
                  data-kt-menu-attach="parent" data-kt-menu-placement="top">
                  <i class="bi bi-plus fs-1"></i>
                </button>
            </div>
            <div ref="menu" @click="toggleShowMenuUpload"
              :class="showMenuUpload ? 'show position-absolute bottom-100' : ''"
              class="menu menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-primary fw-bold py-4 fs-6 w-200px"
              data-kt-menu="true">
              <!-- <div class="menu-item" data-bs-toggle="modal" data-bs-target="#kt_modal_share_product" data-kt-menu-trigger="hover" data-kt-menu-placement="right-start"
              >
                <div class="menu-link py-3">
                  <span class="menu-icon">
                    <i class="bi bi-bag fs-3"></i>
                  </span>
                  <span class="menu-title">{{ $t("share product") }}</span>
                </div>
              </div> -->
              <div class="menu-item">
                <button class="btn p-0 w-100 file-upload-text" data-kt-menu-trigger="hover" @click="shareCatalog()"
                  data-kt-menu-placement="right-start">
                  <div class="menu-link py-3">
                    <span class="menu-icon">
                      <i class="bi bi-book fs-3"></i>
                    </span>
                    <span class="menu-title">{{ $t("share catalog") }}</span>
                  </div>
                </button>
              </div>
              <div class="menu-item" @click="openShareProducts">
                <div class="btn p-0 w-100 file-upload-text" data-kt-menu-trigger="hover"
                  data-kt-menu-placement="right-start">
                  <div class="menu-link py-3">
                    <span class="menu-icon">
                      <i class="bi bi-cart fs-3"></i>
                    </span>
                    <span class="menu-title">{{ $t("Share Products") }}</span>
                  </div>
                </div>
              </div>
              <div class="menu-item">
                <div class="btn p-0 w-100 file-upload-text" @click="openProductDialog"  data-kt-menu-trigger="hover"
                  data-kt-menu-placement="right-start">
                  <div class="menu-link py-3">
                    <span class="menu-icon">
                      <i class="bi bi-bag fs-3"></i>
                    </span>
                    <span class="menu-title">{{ $t("share product") }}</span>
                  </div>
                </div>
              </div>
              <div class="menu-item">
                <label for="images" class=" w-100 file-upload-text" data-kt-menu-trigger="hover"
                  data-kt-menu-placement="right-start">
                  <!--begin::Menu link-->
                  <div class="menu-link py-3">
                    <span class="menu-icon">
                      <i class="bi bi-image fs-3"></i>
                    </span>
                    <span class="menu-title">{{ $t("photo-video") }}</span>
                  </div>
                </label>
              </div>
              <div class="menu-item">
                <div class="btn p-0 w-100 file-upload-text" data-kt-menu-trigger="hover"
                  data-kt-menu-placement="right-start">
                  <!--begin::Menu link-->
                  <div class="menu-link py-3" @click="$refs.documentUploadRef.browseDocument()">
                    <span class="menu-icon">
                      <i class="bi bi-file-earmark-text fs-3"></i>
                    </span>
                    <span class="menu-title">{{ $t("send-file") }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <button @click="() => toggleRecordSection(true)"
            class="btn btn-sm btn-icon bg-gray-200 bg-hover-light-danger text-hover-danger me-1 menu-link w-35px h-35px"
            type="button" data-bs-toggle="tooltip" :title="$t('Send Audio')">
            <i class="bi bi-mic fs-3"></i>
          </button>
        </div>
        <DocumentUpload ref="documentUploadRef" :tenant="tenant" @uploadedDocument="updateUploadedDocument" @updateLoading="updateLoadingDocument" />
        <FileUpload @update:length="updateImages" ref="file_upload" :tenant="tenant"></FileUpload>
        <ShareProduct @share="shareProduct" :locale="locale" ref="shareProduct"></ShareProduct>
		<ShareProducts @shareProducts="shareProductsHandler" ref="shareProducts" :locale="locale"></ShareProducts>
        <button class="btn btn-primary" type="button" data-kt-element="send" :disabled="sendMessageLoading"
          @click="sendMessage">
          <div v-if="sendMessageLoading">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ $t("Sending") }}
          </div>
          <span v-else>{{ $t("Send") }}</span>
        </button>
      </div>
      <div v-if="conversationId && showRecordSection">
        <RecordAudio :conversation="conversationId" />
      </div>
    </div>
  </div>
</template>

<script>
import parsePhoneNumber from "libphonenumber-js";

import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import OrderType from "@/components/whatsapp-managment/includes/types/order.vue";
import AudioType from "@/components/whatsapp-managment/includes/types/audio.vue";
import ImageType from "@/components/whatsapp-managment/includes/types/image.vue";
import VideoType from "@/components/whatsapp-managment/includes/types/video.vue";
import Catalog from "@/components/whatsapp-managment/includes/types/Catalog.vue";
import InteractiveButtons from "@/components/whatsapp-managment/includes/types/InteractiveButtons.vue";
import Flow from "@/components/whatsapp-managment/includes/types/Flow.vue";
import ProductShare from "@/components/whatsapp-managment/includes/types/ProductShare.vue";
import ProductsShare from "@/components/whatsapp-managment/includes/types/ProductsShare.vue";
import MessageWrapper from "@/components/whatsapp-managment/includes/types/MessageWrapper.vue";
import NotSupported from "@/components/whatsapp-managment/includes/types/NotSupported.vue";
import Document from "@/components/whatsapp-managment/includes/types/Document.vue";
import DocumentUpload from "@/components/whatsapp-managment/includes/types/DocumentUpload.vue";
import ReplyMessage from "@/components/whatsapp-managment/includes/ReplyMessages/SendReply/index.vue";
import PaymentResponseWrapper from "@/components/whatsapp-managment/includes/types/PaymentResponseWrapper.vue";
import ReceivedReply from "@/components/whatsapp-managment/includes/ReplyMessages/ReceivedReply/index.vue";
import PaymentDetails from "@/components/whatsapp-managment/includes/ReplyMessages/ReceivedReply/PaymentDetails.vue";
import FileUpload from "@/components/whatsapp-managment/includes/types/fileupload.vue";
import ShareProduct from "@/components/whatsapp-managment/includes/ShareProduct.vue";
import ShareProducts from "@/components/whatsapp-managment/includes/ShareProducts.vue";
import { MessageTypes, InteractiveSubtypes } from "@/components/whatsapp-managment/interfaces.js"
import RecordAudio from "@/components/whatsapp-managment/includes/types/RecordAudio.vue";
import lodash from "lodash";
import Campaign from "@/components/whatsapp-managment/includes/types/Campaign.vue";
import Location from "./types/Location.vue";
import MessageStatus from "./components/MessageStatus";
import ExpiryStatus from "./components/ExpiryStatus";
import MessageOptionsMenu from "./components/MessageOptionsMenu";

export default {
  components: {
    OrderType,
    ReplyMessage,
    MessageWrapper,
    ReceivedReply,
    PaymentResponseWrapper,
    AudioType,
    ImageType,
    Catalog,
    InteractiveButtons,
    Flow,
    ProductShare,
    ProductsShare,
    FileUpload,
    ShareProduct,
    ShareProducts,
    NotSupported,
    PaymentDetails,
    Document,
    DocumentUpload,
    RecordAudio,
    Campaign,
    Location,
    MessageStatus,
    ExpiryStatus,
    VideoType,
    MessageOptionsMenu
  },
  props: {
    conversationId: [Object],
    profile: String,
    isSizePhoneMode: Boolean,
    hasNewMsg: Boolean,
    tenant: 299,
    locale: "ar",
    marketplace: null,
  },
  data() {
    return {
      profile_name: null,
      phone_number_id: null,
      messages: [],
      last_message: null,
      newMessage: "",
      messagesLimit: 10,
      hasNewMessageUnRead: false,
      unsubscribeSnapshot: null,
      unsubscribeStoreSnapshot: null,
      base_url: window.location.origin,
      currentLabel: "",
      clearCurrentLabelTimeOut: "",
      showMenuUpload: false,
      uploadedImagesLength: 0,
      isCustomMessageType: false,
      customMessageType: '',
      catalog_text: "كل ما تبحث عنه في مكان واحد! اكتشف مجموعتنا المتنوعة الآن.	✨ 🛍️ \n Find everything you need seamlessly in one place! Discover our distinctive collection now! ✨🛍️",
      product_text: "🔍 قطعة مميزة بنتظارك. اطلع على تفاصيلها ومميزاتها الآن! \n 🔍 A special item awaits you! Check out its details and features now!",
      dateSeparationObject: null,
      uploadImage: false,
      multiProductShareSection: [],
      multiProductSingleSectionName: 'test name',
      catalog_id: '',
      bodyText: "A test Body Text",
      headerText: "A test Header Text",
      chatExpanded: localStorage.getItem('chatExpanded') == 'true',
      MessageTypes,
      InteractiveSubtypes,
      loadingDocument: false,
      uploadedDocumentData: null,
      activeMenu: null,
    };
  },

  computed: {
    orderLink() {
      return this.$store.getters["Chat/chatMessage"];
    },
    sendMessageLoading() {
      return this.$store.getters["Chat/sendMessageLoading"];
    },
    replyToMessage() {
      return this.$store.getters["Chat/replyToMessage"];
    },
    firebaseMessages() {
      return this.$store.getters["Chat/messages"];
    },
    selectedSingleProduct() {
      return this.$store.getters["WhatsappShare/singleProduct"];
    },
    showRecordSection() {
      return this.$store.getters["Chat/showRecordSection"];
    },
    selectedMultiProduct() {
      return this.$store.getters["WhatsappShare/multiProduct"];
    },
    formattedMobileNumber() {
      let rawInput = this.conversationId.contact_display_name ?? this.conversationId.profile_name;
      if (this.conversationId.profile_name === this.conversationId.id) {
        rawInput = this.conversationId.id;
        if (rawInput.startsWith("00")) {
          rawInput = rawInput.replace("00", "+");
        }
        try {
          const phoneNumber = parsePhoneNumber(
            rawInput.includes("+") ? rawInput : `+${rawInput}`
          );
          const result = phoneNumber.formatInternational();
          return result;
        } catch (error) {
          return rawInput;
        }
      }
      return rawInput;
    },
    waitingForFileMessage() {
        return (this.loadingDocument || this.uploadedDocumentData !== null);
    }
  },
  watch: {
    orderLink(newVal) {
      this.newMessage = newVal.chatMessage;
      this.sendMessage();
    },
    hasNewMsg(newHasNewMsg) {
      const chatMessages = document.getElementById("chatMessages");
      const isAtBottom =
        chatMessages.scrollTop + chatMessages.clientHeight + 5 >=
        chatMessages.scrollHeight;
      if (isAtBottom) {
        this.scrollToBottom(true)
      }
      this.hasNewMessageUnRead = !isAtBottom && this.hasNewMsg;
      if (this.hasNewMsg && isAtBottom) {
        this.makeMessageRead();
      }
    },
    async conversationId(newId, oldId) {
      // make last message unread
      this.makeMessageRead()
      if (this.$refs.file_upload)
        this.$refs.file_upload.reset();
      if (this.$refs.documentUploadRef)
        this.$refs.documentUploadRef.reset();

      // remove reply message if exist
      this.$store.commit("Chat/setReplyToMessage", null);
      if (newId !== oldId) {
        // hide record audio section
        this.toggleRecordSection(false);
        this.messagesLimit = 10;
        this.newMessage = "";
        if (this.unsubscribeStoreSnapshot) {
          await this.unsubscribeStoreSnapshot()
        }
        await this.$store.dispatch("Chat/fetchInitialMessages", newId);
        this.unsubscribeStoreSnapshot = await this.$store.dispatch("Chat/listenForNewMessages", newId);
        this.scrollToBottom(true);
      }
    },
    firebaseMessages(newVal) {
      const result = [];
      let lastDate = null;
      // generate date labels
      newVal.forEach((message, index) => {
        const messageDate = this.formatLabel(message.timestamp);
        if (messageDate != lastDate) {
          result[index] = ({ index, date: messageDate });
          lastDate = messageDate;
        }
      });
      this.dateSeparationObject = result
    },
    selectedMultiProduct(newVal) {
      /* const grouped = lodash.groupBy(newVal, (item) => item?.action?.catalog_id);
      this.multiProductShareSection = lodash.map(grouped, (group) => ({
        title: lodash.get(lodash.first(group), 'extra_data.product_name'),
        product_items: group.map(
          item => ({
            product_retailer_id: item.action.product_retailer_id
          })
        )
      })); */
      const product_items = newVal.map(element => ({
        product_retailer_id: element?.action?.product_retailer_id
      }));
      this.multiProductShareSection = [
        {
          title: this.$t('Share Products Section Title'),
          product_items
        }
      ]
    },
    chatExpanded(newVal) {
      localStorage.setItem('chatExpanded', newVal);
    },
    'conversationId.last_message'(newId) {
      if (newId?.sent_by_merchant) {
        this.scrollToBottom(true)
      }
    },
  },
  methods: {
    convertToHyperlink(content) {
      // Regular expression to match URLs
      const urlPattern = /(https?:\/\/[^\s]+)/g;
      return content?.text?.body?.replace(urlPattern, '<a href="$1" target="_blank">$1</a>');
    },
      showMessageMenuOptions(event) {
          this.activeMenu = event.id;
      },
    updateLoadingDocument(value) {
        this.loadingDocument = value;
    },
    toggleRecordSection(value) {
      this.$store.dispatch("Chat/setShowRecordSection", value)
    },
    openProductDialog() {
		this.$refs['shareProduct'].open()
	},
	openShareProducts() {
		this.$store.dispatch('WhatsappShare/clearMultipleProducts')
		this.$refs['shareProducts'].open()
	},
    updateImages(imagesLength) {
      this.uploadedImagesLength = imagesLength;
    },
    toggleShowMenuUpload() {
      this.showMenuUpload = !this.showMenuUpload;
      // Add event listener when menu is shown
      if (this.showMenuUpload) {
        document.addEventListener('click', this.handleClickOutsideMenu);
      } else {
        // Remove event listener when menu is hidden
        document.removeEventListener('click', this.handleClickOutsideMenu);
      }
    },
    handleClickOutsideMenu(event) {
      // Check if the clicked element is outside the menu
      const menuElement = this.$refs.menu; // Assuming you have a ref for the menu
      if (menuElement && !menuElement.contains(event.target) && !this.$refs.menu_button.contains(event.target)) {
        // Clicked outside the menu, so hide the menu
        this.showMenuUpload = false;
        document.removeEventListener('click', this.handleClickOutsideMenu);
      }
    },
    //in mode phone return to list contact page
    back() {
      this.$emit("back");
    },
    showProfile() {
      this.$emit("show-profile");
    },
    handleScroll() {
      this.firstVisibleMessage();
      this.$nextTick(() => {
        const chatMessages = document.getElementById("chatMessages");
        const isAtBottom =
          chatMessages.scrollTop + chatMessages.clientHeight + 5 >=
          chatMessages.scrollHeight;

        this.hasNewMessageUnRead = !isAtBottom && this.hasNewMsg;
        if (this.hasNewMsg && isAtBottom) {
          // 'chatMessages.scrollTop + chatMessages.clientHeight + 5',chatMessages.scrollTop + chatMessages.clientHeight + 5,
          // 'chatMessages.scrollHeight',chatMessages.scrollHeight,
          // isAtBottom,'this.hasNewMsg',this.hasNewMsg)
          this.makeMessageRead();
        }
      });
    },
    async checkScroll(event) {
      if (event.target.scrollTop === 0) {
        const oldScrollHeight = event.target.scrollHeight;
        await this.$store.dispatch("Chat/fetchMoreMessages", this.conversationId);

        this.$nextTick(() => {
          // Calculate new scroll height after messages have loaded
          const newScrollHeight = event.target.scrollHeight;

          // Adjust the scroll position to maintain the view
          event.target.scrollTop += (newScrollHeight - oldScrollHeight);
        });
      }
    },
    sendOnEnter(event) {
      if (event.shiftKey) {
        this.newMessage += "\n";
        return;
      }
      this.newMessage = this.newMessage.trim();
      this.sendMessage();
    },
    sendPaymentLink(link) {
      this.newMessage = link;
    },
    srollAndRead() {
      this.scrollToBottom(true);
      this.makeMessageRead();
    },
    makeMessageRead() {
      const conversationRef = doc(
        db,
        `tenants/${this.tenant}/conversations`,
        this.conversationId.id
      );
      updateDoc(conversationRef, {
        "last_message.is_new": false,
      });
    },
    scrollToBottom(animate = false) {
      let id = "chatMessages";
      const chatContainer = document.getElementById(id);
      if (chatContainer) {
        if (!animate) {
          chatContainer.scrollTop = chatContainer.scrollHeight;
        } else {
          $(`#${id}`).animate(
            {
              scrollTop: chatContainer.scrollHeight,
            },
            "slow"
          );
        }
      }
    },
    formatTimestamp(seconds) {
      // Determine the timestamp length to guess its unit
      const timestampLength = seconds.toString().length;

      // Assume timestamps with more than 13 digits are in microseconds
      if (timestampLength > 13) {
        // Convert microseconds to milliseconds
        seconds = Math.floor(seconds / 1000);
      } else {
        seconds = seconds * 1000;
      }
      let date = new Date(seconds);
      let options = { hour: "numeric", minute: "numeric" };
      return date.toLocaleTimeString(undefined, options)
        ? date
          .toLocaleTimeString(undefined, options)
          .replace("AM", this.$t("AM"))
          .replace("PM", this.$t("PM"))
        : "";
    },
    formatLabel(timestamp) {
      // Determine the timestamp length to guess its unit
      const timestampLength = timestamp.toString().length;

      // Assume timestamps with more than 13 digits are in microseconds
      if (timestampLength > 13) {
        // Convert microseconds to milliseconds
        timestamp = Math.floor(timestamp / 1000);
      } else {
        timestamp = timestamp * 1000;
      }
      let date = new Date(timestamp);
      let currentDate = new Date();

      if (
        date.getDate() === currentDate.getDate() &&
        date.getMonth() === currentDate.getMonth() &&
        date.getFullYear() === currentDate.getFullYear()
      ) {
        return this.$t("Today").toUpperCase();
      }

      currentDate.setDate(currentDate.getDate() - 1);

      if (
        date.getDate() === currentDate.getDate() &&
        date.getMonth() === currentDate.getMonth() &&
        date.getFullYear() === currentDate.getFullYear()
      ) {
        return this.$t("Yesterday").toUpperCase();
      }

      let day = date.getDate().toString().padStart(2, "0");
      let month = (date.getMonth() + 1).toString().padStart(2, "0");
      let year = date.getFullYear();

      return `${day}/${month}/${year}`;
    },
    async updateUploadedDocument(data) {
        this.uploadedDocumentData = data;
    },
    async sendMessage() {
      // case there is message to send
      if (this.uploadImage) return '';
      if (this.sendMessageLoading) return '';
      if (this.uploadedImagesLength == 0 && this.newMessage.trim() == "" && !this.isCustomMessageType && this.uploadedDocumentData === null) return;
      if(this.loadingDocument) return;
      const messageBasicInfo = {
        tenant_id: this.conversationId.tenantId,
        customer_phone_number: this.conversationId.id,
        customer_profile_name: this.conversationId.profile_name,
      }
      if (this.replyToMessage) { messageBasicInfo.reply_message_id = this.replyToMessage.message_id }
      try {
        // handle image messages
        if (this.uploadedImagesLength > 0) {
          this.uploadImage = true;
          const imagesContent = await this.uploadMessages();
          this.uploadImage = false;

          imagesContent.forEach(async (image) => {
            console.log("image",image);
            await this.$store.dispatch("Chat/sendMessage",
              { data: { ...image, ...messageBasicInfo, type: image?.content?.type.startsWith("video/") ? MessageTypes.Video : MessageTypes.Image } });
          })
        }  else if(this.uploadedDocumentData) {
            // handle document message
            await this.$store.dispatch("Chat/sendMessage", { data: { content: this.uploadedDocumentData, type: MessageTypes.Document, ...messageBasicInfo } });
            this.$refs.documentUploadRef.reset();
        } else if (this.isCustomMessageType) {
          let content = '';
          switch (this.customMessageType) {
            case InteractiveSubtypes.Catalog:
              content = { "type": InteractiveSubtypes.Catalog, "action": { "name": InteractiveSubtypes.Catalog }, "body": { "text": this.catalog_text }, }
              break;

            case InteractiveSubtypes.Product:
              content = { "type": InteractiveSubtypes.Product, "action": this.selectedSingleProduct.product?.action, "body": { "text": this.product_text }, }
              break;
            case InteractiveSubtypes.Products:
              content = {
                type: InteractiveSubtypes.Products,
                header: {
                  type: "text",
                  text: this.$t('Share Products Header')
                },
                body: { text: this.$t('Share Products Body') },
                action: {
                  catalog_id: this.catalog_id,
                  sections: this.multiProductShareSection
                },
              }
              break;
          }
          await this.$store.dispatch("Chat/sendMessage",
            { data: { type: MessageTypes.Interactive, content, "extra_data": this.selectedSingleProduct?.product?.extra_data, ...messageBasicInfo } });
          // Hide Multi Product Menu
          $('#kt_modal_share_products').modal('hide');
        }
        // handle text messages
        else {
          await this.$store.dispatch("Chat/sendMessage",
            { data: { type: MessageTypes.Text, content: this.newMessage, ...messageBasicInfo } });
        }
        // clear related states
        if (!this.isCustomMessageType) {
          this.newMessage = "";
          this.uploadedImagesLength = 0;
          this.loadingDocument = false;
          this.uploadedDocumentData = null;
          await this.$store.dispatch("Chat/clearReplyToMessage");
        }

        this.isCustomMessageType = false;
        this.scrollToBottom(true);
      } catch (error) {
        console.log("An error happened while sending your message: ", error);
      }
    },
    async shareProduct() {
      this.isCustomMessageType = true;
      this.customMessageType = InteractiveSubtypes.Product;
      this.sendMessage()
    },
    async shareProductsHandler(catalog_id) {
      this.catalog_id = catalog_id
      this.isCustomMessageType = true;
      this.customMessageType = InteractiveSubtypes.Products;
      this.sendMessage()
    },
    async shareCatalog() {
      this.isCustomMessageType = true;
      this.customMessageType = InteractiveSubtypes.Catalog;
      this.sendMessage()
    },
    async uploadMessages() {
      const imagesContent = []
      await this.$refs.file_upload.uploadImages(async (url, hidden, type) => {
        imagesContent.push({
          content: {
            url: url,
            caption: hidden ? '' : this.newMessage,
            type: type,
          },
        })
      });
      return imagesContent
    },

    firstVisibleMessage() {
      const chatMessages = document.getElementById("chatMessages");
      for (let index = 0; index < chatMessages.children.length; index++) {
        const item = chatMessages.children[index];
        const firstMessage = item;
        const isViewd = this.isScrolledIntoView(firstMessage);
        const messages_index =
          chatMessages.children[index].getAttribute("data-id-ref");

        if (
          isViewd &&
          messages_index !== null &&
          this.firebaseMessages[messages_index].hasOwnProperty("type")
        ) {
          const currentElement = this.firebaseMessages[messages_index];
          if (this.clearCurrentLabelTimeOut) {
            clearTimeout(this.clearCurrentLabelTimeOut); // Cancel the previous timeout
          }
          if (currentElement.type == "label") {
            this.currentLabel = currentElement.value;
          } else {
            this.currentLabel = this.formatLabel(currentElement.timestamp);
          }
          this.clearCurrentLabelTimeOut = setTimeout(
            this.resetCurrentLabel,
            1500
          );

          return "";
        }
      }
    },
    resetCurrentLabel() {
      this.currentLabel = "";
    },
    isScrolledIntoView(elem) {
      const chatMessages = document.getElementById("chatMessages");

      const docViewTop = chatMessages.scrollTop;
      const docViewBottom = docViewTop + chatMessages.clientHeight;

      const elemTop = elem.offsetTop;
      const elemBottom = elemTop + elem.offsetHeight;

      // Return true if any part of the element is visible
      return elemBottom >= docViewTop && elemTop <= docViewBottom;
    },
    toggleProfile() {
      this.chatExpanded = !this.chatExpanded
      this.$emit("toggleProfile", this.chatExpanded);
    }
  },
  async mounted() {
    const chatMessages = document.getElementById("chatMessages");
    chatMessages.addEventListener("scroll", this.handleScroll);

    //initial state
    if (this.conversationId) {
      this.messagesLimit = 10;
    }
    true;
  },
  beforeDestroy() {
    const chatMessages = document.getElementById("chatMessages");
    chatMessages.removeEventListener("scroll", this.handleScroll);
    if (this.unsubscribeStoreSnapshot) {
      this.unsubscribeStoreSnapshot()
    }
  },
};
</script>

<style scoped lang="scss">
@import './types/styles/chat.scss';

.border-top-right-0 {
  border-radius: 8px 0px 8px 8px;
}

.border-top-left-0 {
  border-radius: 0px 8px 8px 8px;
}

.fs-14px {
  font-size: 14px;
}

.message-error{
    color: #D92D20;
    font-size: 12px;
    margin: 5px 0 0;
}
</style>
